import axiosInstance from '../helpers/axios';
import { TOpsAssignmentList, TOpsEvalOverview } from '../types';

/**
 * /api/ops/reports — instructor load, account instructor history, the
 * exception monitors and the review / wrap record — plus the filtered
 * variants of /api/ops/evals and /api/ops/assignments they lean on.
 *
 * Every seat figure here is a registration (a booked seat); nothing in
 * these types carries an evaluation score next to an instructor.
 */

// ── Instructor load ────────────────────────────────────────────────────────

export type TLoadCounter = { classes: number; hours: number };

export type TInstructorLoadRow = {
  instructorName: string;
  classes: number;
  registrations: number;
  hours: number;
  public: TLoadCounter;
  private: TLoadCounter;
  PD: TLoadCounter;
  BA: TLoadCounter;
  other: TLoadCounter;
};

/** Cost per class in the window — a total and per-class lines, never a name */
export type TLoadCostLine = {
  classId: string;
  className: string | null;
  start: string | null;
  classType: string | null;
  assignments: number;
  hours: number;
  amount: number;
  /** true when any line fell back to the $150/hr · $750 flat default */
  defaulted: boolean;
};

export type TLoadCost = {
  total: number;
  classesWithAssignments: number;
  classesDefaulted: number;
  basis: string;
  lines: TLoadCostLine[];
};

export type TInstructorLoad = {
  window: { from: string; to: string; label: 'next month' | 'custom' };
  basis: { classes: string; registrations: string; hours: string };
  totals: { classes: number; hours: number; registrations: number };
  unassigned: { classes: number; hours: number; registrations: number };
  items: TInstructorLoadRow[];
  cost: TLoadCost;
};

export type TLoadWindow = { from?: string; to?: string };

export const getInstructorLoad = async (params: TLoadWindow) =>
  (
    await axiosInstance.get<TInstructorLoad>('ops/reports/instructor-load', {
      params,
    })
  ).data;

// ── Account instructors ────────────────────────────────────────────────────

export type TAccountInstructorRow = {
  instructorName: string;
  classes: number;
  upcoming: number;
  hours: number;
  firstDate: string | null;
  lastDate: string | null;
  lastClassName: string | null;
  assignments: number;
};

export type TAccountInstructors = {
  accountId: string;
  accountName: string | null;
  totalClasses: number;
  unassignedClasses: number;
  items: TAccountInstructorRow[];
};

export const getAccountInstructors = async (accountId: string) =>
  (
    await axiosInstance.get<TAccountInstructors>(
      'ops/reports/account-instructors',
      { params: { accountId } },
    )
  ).data;

// ── Exceptions ─────────────────────────────────────────────────────────────

export type TExceptionClass = {
  id: string;
  name: string | null;
  classNumber: string | null;
  classType: string | null;
  status: string | null;
  start: string | null;
  daysOut: number | null;
  instructorName: string | null;
  accountId: string | null;
  accountName: string | null;
  productFamily: string | null;
  localOrigin: boolean;
};

export type TExceptionPrepCall = TExceptionClass & { prepCallStatus: string };

export type TExceptionProgram = {
  id: string;
  programId: string;
  programName: string | null;
  contractNumber: string | null;
  programStatus: string | null;
  accountId: string | null;
  accountName: string | null;
  startDate: string | null;
  endDate: string | null;
  daysToEnd: number | null;
  invested: number | null;
  used: number | null;
  remainingBalance: number | null;
};

export type TExceptionReviewDue = TExceptionProgram & {
  lastReviewAt: string | null;
  daysSinceReview: number | null;
};

export type TExceptionWatch = {
  id: string;
  accountId: string;
  accountName: string | null;
  lastSessionAt: string | null;
  daysSinceLastSession: number | null;
  remainingBalance: number;
  programs: number;
  programIds: string[];
  nextProgramEnd: string | null;
};

type TList<T> = { count: number; checked?: number; basis: string; items: T[] };

export type TReportExceptions = {
  generatedAt: string;
  cap: number;
  horizons: {
    prepCallDays: number;
    reviewDays: number;
    watchlistDays: number;
    wrapDays: number;
  };
  commitsWithoutReservation: TList<TExceptionClass> & {
    portalBorn: TList<TExceptionClass>;
  };
  classesWithoutAssignment: TList<TExceptionClass>;
  prepCallsDue: TList<TExceptionPrepCall> & { days: number };
  accountReviewsDue: TList<TExceptionReviewDue> & { days: number };
  watchlist: TList<TExceptionWatch> & { days: number };
  wrapsDue: TList<TExceptionProgram> & { days: number };
  /** "Event and Reservation Out of Sync" — mirror mode only. */
  reservationDrift: {
    count: number;
    skipped: boolean;
    reason: string | null;
    byField: Record<string, number>;
    items: {
      id: string;
      name: string | null;
      accountName: string | null;
      start: string | null;
      status: string | null;
      instructor: string | null;
      portalEdited: boolean;
      staleSide: 'reservation' | 'unknown';
      fields: string;
      drift: {
        field: string;
        portal: string | null;
        reservation: string | null;
      }[];
    }[];
  };
  /** Holds about to run out (7 days). */
  holdsExpiring: {
    count: number;
    items: {
      id: string;
      name: string | null;
      start: string | null;
      instructor: string | null;
      accountName: string | null;
      holdExpiresAt: string | null;
      expired: boolean;
      note: string | null;
    }[];
  };
};

export const getReportExceptions = async () =>
  (await axiosInstance.get<TReportExceptions>('ops/reports/exceptions')).data;

// ── Reviews & wraps ────────────────────────────────────────────────────────

export type TReviewKind = 'review' | 'wrap';

export type TAccountReview = {
  id: string;
  accountId: string;
  accountName: string | null;
  programId: string | null;
  kind: TReviewKind;
  at: string | null;
  nextAt: string | null;
  notes: string | null;
  by: string | null;
};

export type TAccountReviewCreate = {
  accountId: string;
  programId?: string;
  kind: TReviewKind;
  notes?: string;
  nextAt?: string;
};

export const recordAccountReview = async (body: TAccountReviewCreate) =>
  (
    await axiosInstance.post<TAccountReview>(
      'ops/reports/account-reviews',
      body,
    )
  ).data;

export const getAccountReviews = async (params: {
  accountId?: string;
  programId?: string;
}) =>
  (
    await axiosInstance.get<{
      accountId: string | null;
      programId: string | null;
      accountName: string | null;
      items: TAccountReview[];
    }>('ops/reports/account-reviews', { params })
  ).data;

// ── Program utilisation (T5.08) ────────────────────────────────────────────

export type TUtilisationBucket = {
  key: string;
  registrations: number;
  attended: number;
  noAttend: number;
};

export type TProgramUtilisation = {
  generatedAt: string;
  program: {
    id: string;
    name: string | null;
    number: string | null;
    accountName: string | null;
    status: string | null;
    subtype: string | null;
    startDate: string | null;
    endDate: string | null;
  };
  balance: {
    invested: number | null;
    used: number | null;
    remaining: number | null;
    valueConsumed: number;
    teamPass: boolean;
    usedPct: number | null;
  };
  members: { total: number; enabled: number };
  classes: {
    held: number;
    upcoming: number;
    cancelled: number;
    private: number;
    public: number;
  };
  registrations: {
    total: number;
    attended: number;
    noAttend: number;
    upcoming: number;
    cancelled: number;
    attendancePct: number | null;
  };
  byMonth: TUtilisationBucket[];
  byCourse: TUtilisationBucket[];
  topCourses: TUtilisationBucket[];
  basis: Record<string, string>;
};

export const getProgramUtilisation = async (programId: string) =>
  (
    await axiosInstance.get<TProgramUtilisation>(
      'ops/reports/program-utilisation',
      { params: { programId } },
    )
  ).data;

// ── Cancellation review (T3.44) ────────────────────────────────────────────

export type TCancellationGroup = {
  accountId: string | null;
  accountName: string | null;
  count: number;
  learners: string[];
  learnersOmitted: number;
  courses: string[];
  lastDate: string | null;
  classes: number;
  flagged: boolean;
  registrationIds: string[];
  openCase: {
    id: string;
    caseNumber: string | null;
    createdDate: string | null;
  } | null;
};

export type TCancellations = {
  generatedAt: string;
  window: { days: number; from: string; to: string };
  escalateAt: number;
  cap: number;
  totals: { registrations: number; accounts: number; flagged: number };
  basis: { registrations: string; flagged: string };
  items: TCancellationGroup[];
};

export const getCancellations = async (params: {
  days?: number;
  escalateAt?: number;
}) =>
  (
    await axiosInstance.get<TCancellations>('ops/reports/cancellations', {
      params: compact(params),
    })
  ).data;

export const escalateCancellations = async (body: {
  accountId: string;
  days?: number;
  escalateAt?: number;
}) =>
  (
    await axiosInstance.post<{
      id: string;
      caseNumber: string;
      accountId: string;
      accountName: string | null;
      count: number;
      priority: string;
    }>('ops/reports/cancellations/escalate', body)
  ).data;

// ── Invoice queue (T6.07) ──────────────────────────────────────────────────

export type TInvoiceKind = 'class' | 'travel' | 'program';

export type TInvoiceQueueItem = {
  kind: TInvoiceKind;
  id: string;
  name: string | null;
  classNumber: string | null;
  classType: string | null;
  status: string | null;
  date: string | null;
  accountId: string | null;
  accountName: string | null;
  amount: number | null;
  reason: string;
  localOrigin: boolean;
};

export type TInvoiceQueue = {
  generatedAt: string;
  days: number;
  note: string;
  basis: { classes: string; travel: string; programs: string };
  classes: { count: number; items: TInvoiceQueueItem[] };
  travel: { count: number; items: TInvoiceQueueItem[] };
  programs: { count: number; items: TInvoiceQueueItem[] };
  total: number;
};

export const getInvoiceQueue = async (params: { days?: number }) =>
  (
    await axiosInstance.get<TInvoiceQueue>('ops/reports/invoice-queue', {
      params: compact(params),
    })
  ).data;

export const markInvoiceDone = async (
  kind: TInvoiceKind,
  id: string,
  note?: string,
) =>
  (
    await axiosInstance.post<{
      kind: TInvoiceKind;
      id: string;
      invoicedAt: string;
      note: string | null;
    }>(`ops/reports/invoice-queue/${kind}/${encodeURIComponent(id)}/done`, {
      note,
    })
  ).data;

/** Set or clear the class-level "invoice directly" override. */
export const setClassInvoiceDirectly = async (
  classId: string,
  invoiceDirectly: boolean,
) =>
  (
    await axiosInstance.post<{
      id: string;
      invoiceDirectly: boolean;
      source: 'portal';
    }>(`ops/reports/invoice-queue/flag/${encodeURIComponent(classId)}`, {
      invoiceDirectly,
    })
  ).data;

// ── Filtered evals / assignments ───────────────────────────────────────────

export type TEvalListFilters = {
  q?: string;
  from?: string;
  to?: string;
  type?: 'Public' | 'Private' | '';
  accountId?: string;
};

export const getOpsEvalsFiltered = async (
  params: TEvalListFilters & { page?: number },
) =>
  (
    await axiosInstance.get<TOpsEvalOverview>('ops/evals', {
      params: compact(params),
    })
  ).data;

export const getOpsAssignmentsFiltered = async (params: {
  instructor?: string;
  accountId?: string;
  q?: string;
  upcoming?: boolean;
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}) =>
  (
    await axiosInstance.get<TOpsAssignmentList>('ops/assignments', {
      params: compact({
        ...params,
        upcoming: params.upcoming ? 'true' : undefined,
      }),
    })
  ).data;

// ── CSV downloads ──────────────────────────────────────────────────────────

/**
 * Fetch a report as CSV (the token travels in the header, so a plain link
 * cannot do it) and hand it to the browser as a file.
 */
export const downloadCsv = async (
  path: string,
  params: Record<string, unknown>,
  filename: string,
) => {
  const response = await axiosInstance.get(path, {
    params: compact({ ...params, format: 'csv' }),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** Drop empty filters so the query string stays readable. */
function compact<T extends Record<string, unknown>>(o: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(o) as (keyof T)[]).forEach((k) => {
    const v = o[k];
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
}

/** `YYYY-MM-DD` for a date, local calendar. */
export const dayString = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** The last seven days, ending today — the weekly report's default. */
export const lastSevenDays = () => {
  const to = new Date();
  const from = new Date(to.getTime() - 6 * 864e5);
  return { from: dayString(from), to: dayString(to) };
};
